import { assert } from '@varos/util-typescript';
import { IntegrationLoaderBaseConfig } from '../../../base';
import { CallUpsertLoader } from './callUpsertInterface';
import { CallUpsertAggregate } from './callUpsertModel';
import { useMemo } from 'react';
import { CallAggregate } from '@varos/call-core';
import { AssistantContext } from '@varos/assistant-react';

export function createCallUpsertLoader(
    config: IntegrationLoaderBaseConfig
): CallUpsertLoader {
    const { repository } = config;
    return {
        useLoad(context, props): CallUpsertAggregate {
            const integrationQuery = repository.platform.integration.useRetrieve(
                context,
                props.integration.id,
                {
                    suspense: true,
                }
            );
            assert(integrationQuery.status === 'success', 'expected suspense');

            const definitionQuery = repository.platform.integrationDefinition.useRetrieve(
                context,
                integrationQuery.data.definition_id,
                {
                    suspense: true,
                }
            );
            assert(definitionQuery.status === 'success', 'expected suspense');

            const solutionQuery = repository.platform.integrationTool.useFind(
                context,
                {
                    integrationDefinitionId: integrationQuery.data.definition_id,
                },
                {
                    suspense: true,
                }
            );

            assert(solutionQuery.status === 'success', 'expected suspense');
            const {
                data: [solution = null, ...rest],
            } = solutionQuery;

            assert(rest.length === 0, 'expected no other solutions');

            const solutionConfig = repository.platform.integrationToolConfig.useRetrieve(
                context,
                solution?.id!,
                {
                    suspense: true,
                    enabled: !!solution,
                }
            );

            assert(solutionConfig.status === 'success', 'expected suspense');

            // Load messages for this thread
            const messagesQuery = repository.assistant.local.message.useFind(
                context as AssistantContext,
                { thread: props.thread.id, limit: 25 },
                {
                    suspense: true,
                    staleTime: 0,
                    refetchOnMount: true,
                    refetchOnWindowFocus: true,
                    refetchOnReconnect: true,
                }
            );
            assert(messagesQuery.status === 'success', 'expected suspense for messages');

            // Load TURN credentials for WebRTC communication
            const turnQuery = repository.assistant.turn.useLookup(
                context as AssistantContext,
                {},
                {
                    suspense: true,
                    staleTime: 10 * 60 * 1000, // 10 minutes
                    cacheTime: 15 * 60 * 1000, // 15 minutes
                    refetchOnWindowFocus: false,
                }
            );
            assert(turnQuery.status === 'success', 'expected suspense for TURN');

            const call = useMemo((): CallAggregate => {
                return {
                    solution: solution,
                    config: solutionConfig.data,
                    context: {
                        transcript: messagesQuery.data.flatMap((item) => {
                            const parts = item.parts.flatMap((part) =>
                                part.type === 'text' ? [part.text] : []
                            );
                            if (parts.length === 0) {
                                return [];
                            }
                            return [
                                {
                                    role:
                                        item.role === 'system' ? 'assistant' : item.role,
                                    text: parts.join('\n'),
                                },
                            ];
                        }),
                        configuration: {
                            interviewer: {
                                name: definitionQuery.data.display_name,
                                description: definitionQuery.data.description,
                            },
                            purpose: props.tool.args.purpose,
                            context: props.tool.args.context,
                            deliverables: props.tool.args.deliverables,
                            steps: props.tool.args.process.steps,
                            questions: props.tool.args.questions,
                        },
                        dump: {
                            ...props.tool.args,
                        },
                    },
                };
            }, [messagesQuery.data, definitionQuery.data, props.tool.args]);

            return {
                integration: integrationQuery.data,
                definition: definitionQuery.data,
                tool: props.tool,
                call,
                turnCredentials: turnQuery.data,
            };
        },
    };
}
